import React, { useEffect, useState } from 'react'
import Header from '../comman/Header'
import Footer from '../comman/Footer'
import { apiRequest } from '../../utils/config/apiRequest'
import { apiUrl } from '../../utils/endPoints/apiUrl'
import './accountDelete.css'

export default function AccountDelete() {
    const [step, setStep] = useState(1)

    const [email, setEmail] = useState('')
    const [otp, setOtp] = useState('')
    const [otpToken, setOtpToken] = useState('')

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [message, setMessage] = useState('')

    useEffect(() => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth',
        })
    }, [])

    /**
     * Send OTP
     */
    const sendOTP = async () => {
        if (!email.trim()) {
            setError('Please enter your email')
            return
        }

        try {
            setLoading(true)
            setError('')
            setMessage('')

            const { data, status, message } = await apiRequest({
                endUrl: apiUrl.mobileOtp,
                method: 'POST',
                body: {
                    email: email.trim(),
                    otpFor: 'ACCOUNT_DELETE',
                },
            })

            if (status) {
                setOtpToken(data?.otpToken || '')
                setOtp('')
                setStep(2)
                setMessage('OTP has been sent to your email')
            } else {
                setError(message || 'Unable to send OTP')
            }
        } catch (err) {
            console.error(err)
            setError('Something went wrong. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    /**
     * Verify OTP
     *
     * Successful OTP verification also deletes the account.
     */
    const verifyOTP = async () => {
        if (!otp) {
            setError('Please enter OTP')
            return
        }

        if (otp.length !== 4) {
            setError('Please enter a valid 4-digit OTP')
            return
        }

        if (!otpToken) {
            setError('OTP session has expired. Please request a new OTP.')
            return
        }

        try {
            setLoading(true)
            setError('')
            setMessage('')

            const { status, message } = await apiRequest({
                endUrl: apiUrl.verifyotp,
                method: 'POST',
                body: {
                    otp,
                    otpToken,
                },
            })

            if (status) {
                // OTP verification successfully deletes the account
                setStep(3)
                setMessage('')
            } else {
                setError(message || 'Invalid OTP')
            }
        } catch (err) {
            console.error(err)
            setError('Something went wrong. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    /**
     * Go back to email step
     */
    const goBack = () => {
        setError('')
        setMessage('')
        setOtp('')
        setOtpToken('')
        setStep(1)
    }

    return (
        <div className="account-delete-page">
            <Header />

            {/* Hero */}
            <section className="delete-hero">
                <div className="hero-decoration hero-decoration-one" />
                <div className="hero-decoration hero-decoration-two" />

                <div className="hero-content">
                    <div className="delete-icon">
                        <svg
                            width="32"
                            height="32"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M9 3h6m-7 4h8m-9 0v13h10V7M10 11v6m4-6v6"
                            />
                        </svg>
                    </div>

                    <h1>Delete Account</h1>

                    <p>
                        We are sorry to see you go. Follow the steps below to
                        permanently delete your account.
                    </p>
                </div>
            </section>

            {/* Main */}
            <main className="delete-container">

                {/* Progress */}
                {step !== 3 && (
                    <div className="progress-container">
                        <ProgressStep
                            number="1"
                            label="Email"
                            active={step >= 1}
                        />

                        <div
                            className={`progress-line ${
                                step >= 2 ? 'active' : ''
                            }`}
                        />

                        <ProgressStep
                            number="2"
                            label="Verify OTP"
                            active={step >= 2}
                        />
                    </div>
                )}

                {/* Card */}
                <div className="delete-card">

                    {/* Step 1 - Email */}
                    {step === 1 && (
                        <div className="step-content">
                            <div className="step-header">
                                <div className="step-icon">
                                    <svg
                                        width="26"
                                        height="26"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.7"
                                    >
                                        <rect
                                            x="3"
                                            y="5"
                                            width="18"
                                            height="14"
                                            rx="2"
                                        />

                                        <path
                                            d="M3 7l9 6 9-6"
                                        />
                                    </svg>
                                </div>

                                <h2>Verify your account</h2>

                                <p>
                                    Enter the email address associated with
                                    your account. We'll send you an OTP to
                                    verify your identity.
                                </p>
                            </div>

                            <div className="form-group">
                                <label htmlFor="email">
                                    Email Address
                                </label>

                                <div className="email-input">
                                    <input
                                        id="email"
                                        type="email"
                                        value={email}
                                        placeholder="Enter your email"
                                        autoComplete="email"
                                        onChange={(e) => {
                                            setEmail(e.target.value)
                                            setError('')
                                            setMessage('')
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                sendOTP()
                                            }
                                        }}
                                    />
                                </div>
                            </div>

                            <button
                                className="primary-button"
                                onClick={sendOTP}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner" />
                                        Sending OTP...
                                    </>
                                ) : (
                                    'Send OTP'
                                )}
                            </button>
                        </div>
                    )}

                    {/* Step 2 - OTP */}
                    {step === 2 && (
                        <div className="step-content">
                            <div className="step-header">
                                <div className="step-icon">
                                    <svg
                                        width="26"
                                        height="26"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.7"
                                    >
                                        <rect
                                            x="3"
                                            y="5"
                                            width="18"
                                            height="14"
                                            rx="2"
                                        />

                                        <path d="M7 9h.01M11 9h.01M15 9h.01M7 13h.01M11 13h.01M15 13h.01" />
                                    </svg>
                                </div>

                                <h2>Verify OTP</h2>

                                <p>
                                    Enter the 4-digit OTP sent to
                                </p>

                                <strong>{email}</strong>
                            </div>

                            <div className="form-group">
                                <label htmlFor="otp">
                                    Verification Code
                                </label>

                                <input
                                    id="otp"
                                    className="otp-input"
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    value={otp}
                                    maxLength={4}
                                    placeholder="0000"
                                    onChange={(e) => {
                                        setOtp(
                                            e.target.value.replace(/\D/g, '')
                                        )
                                        setError('')
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            verifyOTP()
                                        }
                                    }}
                                />
                            </div>

                            <button
                                className="primary-button"
                                onClick={verifyOTP}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner" />
                                        Verifying...
                                    </>
                                ) : (
                                    'Verify OTP'
                                )}
                            </button>

                            <button
                                className="secondary-button"
                                onClick={sendOTP}
                                disabled={loading}
                            >
                                Resend OTP
                            </button>

                            <button
                                className="back-button"
                                onClick={goBack}
                                disabled={loading}
                            >
                                ← Change email
                            </button>
                        </div>
                    )}

                    {/* Success */}
                    {step === 3 && (
                        <div className="success-content">
                            <div className="success-icon">
                                <svg
                                    width="42"
                                    height="42"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M5 13l4 4L19 7"
                                    />
                                </svg>
                            </div>

                            <h2>Account Deleted</h2>

                            <p>
                                Your account has been successfully deleted.
                                Thank you for using our service.
                            </p>
                        </div>
                    )}

                    {/* Error */}
                    {error && (
                        <div className="alert error-alert">
                            {error}
                        </div>
                    )}

                    {/* Message */}
                    {message && step !== 3 && (
                        <div className="alert success-alert">
                            {message}
                        </div>
                    )}
                </div>
            </main>

            <Footer />
        </div>
    )
}

function ProgressStep({ number, label, active }) {
    return (
        <div className="progress-step">
            <div
                className={`progress-circle ${
                    active ? 'active' : ''
                }`}
            >
                {number}
            </div>

            <span>{label}</span>
        </div>
    )
}